<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcAddressList',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    parent: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const container = ref(null);
  const items = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    container: container.value,
    items: items.value,
    ux: ux.value,
  }));

  const $$load_container = () => {
    return props.parent.addresses;
  };
  const $$load_items = () => {
    return container.value.data;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'container' },
      { name: 'items', dependsOn: ['container'] },
      { name: 'ux' },
    ],
    loaders: { container: $$load_container, items: $$load_items },
    ux: {},
    dataRefs: { container, items, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.parent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent');
    }
  );

  const act_2095089031469157_emit_dwg8rs5jo_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_8hefld1vq_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_2095089031469157_showModal_e1n19ind1_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Address',
      props: {
        parent: props.parent,
      },
    };
  };
  const act_294572930540096_emit_v72452j8i_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_294572930540096_close_7j29wcn2f_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_294572930540096_showModal_9fw82q6d9_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Address',
      props: {
        parent: props.parent,
        item: $event.scope.item,
      },
    };
  };
  const act_3365375699802249_rest_6jcybylvm_getActionArgs = async ($event) => {
    return {
      method: 'delete',
      container: container.value,
      resource: $event.scope.item,
    };
  };
  const act_3365375699802249_rest_6jcybylvm_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };

  const act_7315092382398562_reloadComponentData_jxool0132 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_ss6u0kthm =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_jxool0132,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_ss6u0kthm],
    event: 'reload',
  });
  const act_2095089031469157_emit_dwg8rs5jo = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_dwg8rs5jo_getActionArgs,
  });
  const inst_evh_2095089031469157_close_8hefld1vq =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_dwg8rs5jo,
      event: 'close',
      when: inst_evh_2095089031469157_close_8hefld1vq_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_e1n19ind1 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_e1n19ind1_getActionArgs,
      displayName: 'create',
      events: [inst_evh_2095089031469157_close_8hefld1vq],
    });
  const inst_evh_2095089031469157_clickPreventStop_a3f813t57 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_e1n19ind1,
      event: 'click.prevent.stop',
      displayName: 'create',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_a3f813t57],
      event: 'click.prevent.stop',
    });
  const act_294572930540096_emit_v72452j8i = new actionTypes.EmitAction({
    actionArgs: act_294572930540096_emit_v72452j8i_getActionArgs,
  });
  const inst_evh_294572930540096_close_7j29wcn2f =
    new actionTypes.EventHandlerImpl({
      action: act_294572930540096_emit_v72452j8i,
      event: 'close',
      when: inst_evh_294572930540096_close_7j29wcn2f_getWhen,
      displayName: 'emit',
    });
  const act_294572930540096_showModal_9fw82q6d9 =
    new actionTypes.ShowModalAction({
      actionArgs: act_294572930540096_showModal_9fw82q6d9_getActionArgs,
      events: [inst_evh_294572930540096_close_7j29wcn2f],
    });
  const inst_evh_294572930540096_clickPreventStop_2yfy8zwf9 =
    new actionTypes.EventHandlerImpl({
      action: act_294572930540096_showModal_9fw82q6d9,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_294572930540096_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_294572930540096_clickPreventStop_2yfy8zwf9],
      event: 'click.prevent.stop',
    });
  const act_3365375699802249_rest_6jcybylvm = new actionTypes.RestAction({
    actionArgs: act_3365375699802249_rest_6jcybylvm_getActionArgs,
    confirm: act_3365375699802249_rest_6jcybylvm_getConfirm,
  });
  const inst_evh_3365375699802249_clickPreventStop_g8z06xf8m =
    new actionTypes.EventHandlerImpl({
      action: act_3365375699802249_rest_6jcybylvm,
      event: 'click.prevent.stop',
      displayName: 'rest',
    });
  const inst_evh_3365375699802249_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3365375699802249_clickPreventStop_g8z06xf8m],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_ss6u0kthm,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_8hefld1vq,
    inst_evh_2095089031469157_clickPreventStop_a3f813t57,
    create: inst_evh_2095089031469157_clickPreventStop_a3f813t57,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_294572930540096_close_7j29wcn2f,
    inst_evh_294572930540096_clickPreventStop_2yfy8zwf9,
    inst_evh_294572930540096_clickPreventStop,
    inst_evh_3365375699802249_clickPreventStop_g8z06xf8m,
    inst_evh_3365375699802249_clickPreventStop,
  };

  const evh_7315092382398562_reload_ss6u0kthm = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_ss6u0kthm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_8hefld1vq = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_8hefld1vq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_a3f813t57 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_a3f813t57.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_294572930540096_close_7j29wcn2f = async (event, scope) => {
    return await actions.inst_evh_294572930540096_close_7j29wcn2f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_294572930540096_clickPreventStop_2yfy8zwf9 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_294572930540096_clickPreventStop_2yfy8zwf9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_294572930540096_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_294572930540096_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3365375699802249_clickPreventStop_g8z06xf8m = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3365375699802249_clickPreventStop_g8z06xf8m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3365375699802249_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3365375699802249_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ gettext('Addresses') }}
          </strong>
          <ItpButton
            :text="gettext('Add address...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!parent.isReadOnly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <BListGroupItem v-if="!items || !items.length">
          <span>
            {{ gettext('No addresses have been added yet.') }}
          </span>
        </BListGroupItem>
        <BListGroupItem
          :key="item.id"
          class="d-flex flex-column"
          v-for="item in _.orderBy(
            items,
            ['is_default', 'created_at'],
            ['desc', 'desc']
          )"
        >
          <ItpBox spacing="pb-1">
            <UcAddressCard :item="item" display-default-flag> </UcAddressCard>
            <BDropdown
              size="sm"
              toggle-class="btn-default-borderless"
              no-caret
              variant="none"
              class="ms-auto mb-auto"
              v-if="!item.locked"
            >
              <template #button-content>
                <ItpIcon fa="ellipsis-v" class="mx-1"> </ItpIcon>
              </template>
              <BDropdownItem
                @click.prevent.stop="
                  evh_294572930540096_clickPreventStop($event, { item })
                "
              >
                <ItpText :text="pgettext('Command', 'Edit')"> </ItpText>
              </BDropdownItem>
              <BDropdownItem
                v-if="!item.locked"
                @click.prevent.stop="
                  evh_3365375699802249_clickPreventStop($event, { item })
                "
              >
                <ItpText :text="pgettext('Command', 'Delete')"> </ItpText>
              </BDropdownItem>
            </BDropdown>
          </ItpBox>
          <div class="d-flex align-items-end">
            <ItpText muted small>
              {{ datetime(item.created_at) }}
            </ItpText>
            <small class="text-muted text-nowrap mx-2">
              {{ username(item.updated_by || item.created_by) }}
            </small>
            <div
              class="ms-2 d-flex align-self-end flex-grow-1 justify-content-end"
            >
              <ItpIcon
                :title="gettext('Locked')"
                name="fa-lock"
                class="text-muted"
                v-if="item.locked"
              >
              </ItpIcon>
            </div>
          </div>
        </BListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
